import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { customersKey, machineInfosKey, productsKey, tokenKey } from './constant';
import { toast } from 'react-toastify';
import { useClient } from './connect_api';
import { AuthService, OrganizationService, OrganizationInfo } from './api/api_pb';

interface AuthContextType {
    token: string | null;
    organizationInfo: OrganizationInfo | null;
    loading: boolean;
    error: string | null;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(localStorage.getItem(tokenKey));
    const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const authClient = useClient(AuthService);
    const organizationClient = useClient(OrganizationService);

    const fetchOrganizationInfo = useCallback(async (authToken: string) => {
        setLoading(true);
        setError(null);
        try {
          const response = await organizationClient.getMyOrganizationInfo({});
          if (response.organizationInfo) {
            setOrganizationInfo(response.organizationInfo);
          } else {
            setError('No organization data returned');
            toast.error('No organization data found');
          }
        } catch (err) {
          console.error('Failed to fetch organization info:', err);
          setError('Failed to fetch organization data');
          toast.error('Failed to load organization data');
        } finally {
          setLoading(false);
        }
      }, [organizationClient]);

    useEffect(() => {
        const storedToken = localStorage.getItem(tokenKey);
        if (storedToken) {
          setToken(storedToken);
          fetchOrganizationInfo(storedToken);
        } else {
          setLoading(false);
        }
      }, [fetchOrganizationInfo]);

    const login = async (username: string, password: string) => {
        try {
            const response = await authClient.login({
                email: username,
                password: password,
            });
            localStorage.setItem(tokenKey, response.token);
            setToken(response.token);
            window.dispatchEvent(new Event('storage'));
            await fetchOrganizationInfo(response.token);
            toast.dismiss();
            toast.success('Login successful!');
        } catch (error) {
            localStorage.removeItem(tokenKey);
            setToken(null);
            setOrganizationInfo(null);
            setError('Login failed');
            toast.dismiss();
            toast.error('Login failed. Please check your credentials.');
            throw error;
        }
    };

    const logout = () => {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(customersKey);
        localStorage.removeItem(productsKey);
        localStorage.removeItem(machineInfosKey);
        setToken(null);
        setOrganizationInfo(null);
        setError(null);
        toast.dismiss();
        toast.info('Logged out successfully');
        window.location.reload();
    };

    return (
        <AuthContext.Provider value={{ token, organizationInfo, loading, error, login, logout  }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    // console.log('context', context);
    return context;
};
